// src/pages/comercial/CriarObjetivos.js
import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Card,
  CircularProgress,
  Tooltip,
  ClickAwayListener,
  TextField,
  Dialog,
  Autocomplete,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";

import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import CriarObjetivosAccMulti from "pages/comercial/components/CriarObjetivosAccMulti";
import CriarObjetivosAccClientes from "pages/comercial/components/CriarObjetivosAccClientes";
import CriarObjetivosDialogEquipas from "pages/comercial/components/CriarObjetivosDialogEquipas";
import CriarObjetivosDialogIndividuais from "pages/comercial/components/CriarObjetivosDialogIndividuais";

function toRef(x) {
  // ✅ suporta strings e números diretamente (ex: "001", "OFF_TRADE")
  if (x === null || x === undefined) return "";
  if (typeof x === "string" || typeof x === "number") return x.toString();

  return (x?.ref ?? x?.Ref ?? x?.value ?? x?.valor ?? x?.id ?? "").toString();
}

function toNome(x) {
  return (
    x?.nome ??
    x?.Nome ??
    x?.designacao ??
    x?.Designacao ??
    x?.valor ??
    x?.value ??
    x?.ref ??
    ""
  ).toString();
}

const refsFromArr = (arr) =>
  Array.isArray(arr) ? arr.map((x) => toRef(x).trim()).filter(Boolean) : [];

const intersect = (prev, valid) =>
  Array.isArray(prev) ? prev.filter((r) => valid.includes(r)) : [];

const nextSelectionByAllState = (prevSel, prevAll, nextAll) => {
  const prevAllArr = Array.isArray(prevAll) ? prevAll : [];
  const prevSelArr = Array.isArray(prevSel) ? prevSel : [];
  const nextAllArr = Array.isArray(nextAll) ? nextAll : [];

  // se antes não havia opções:
  // - se já havia seleção (vinda do objetivo), preserva essa seleção (interseção)
  // - se não havia seleção, assume "tudo"
  if (prevAllArr.length === 0) {
    return prevSelArr.length ? intersect(prevSelArr, nextAllArr) : nextAllArr;
  }

  const wasAll = prevSelArr.length === prevAllArr.length;

  // se estava "tudo" => mantém "tudo" (mas com as novas opções)
  if (wasAll) return nextAllArr;

  // se estava parcial (inclui vazio) => preserva apenas as válidas
  return intersect(prevSelArr, nextAllArr);
};

export default function CriarObjetivos({ embed = false, idObjetivo = null, onClose, onSaved }) {
  const { showAlert } = useAlert();
  const { temPermissao, temFuncao } = useAuth();

  const editando = Number(idObjetivo || 0) > 0;
  const [edicaoCarregada, setEdicaoCarregada] = useState(false);

  const [openConfirmDelEquipa, setOpenConfirmDelEquipa] = useState(false);
  const [loadingDelEquipa, setLoadingDelEquipa] = useState(false);

  //const podeGravar = temPermissao("Menu Administração", "w");
  const podeGerir =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Gestão Objetivos");

  const [loading, setLoading] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState("familias");
  const [objetivoNomeTitulo, setObjetivoNomeTitulo] = useState("");

  const [openAvisoDetalhe, setOpenAvisoDetalhe] = useState(false);

  const apagarEquipa = async () => {
    // criação (draft): só limpa localmente
    if (!editando) {
      setEquipaDraft(null);
      setOpenConfirmDelEquipa(false);
      showAlert("Equipa removida (draft).", "success");
      return;
    }

    // edição: apaga no Postgres pelo idobjetivo (o endpoint já existe)
    if (!idObjetivo) {
      showAlert("ID do objetivo inválido.", "error");
      return;
    }

    setLoadingDelEquipa(true);
    try {
      const [payload, err, meta] = await handleApi(
        api.post("Comercial/DelObjetivoEquipa", { idobjetivo: Number(idObjetivo) })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      setEquipaDraft(null);
      setOpenConfirmDelEquipa(false);
      showAlert(meta?.message || "Equipa apagada.", meta?.severity || "success");
    } finally {
      setLoadingDelEquipa(false);
    }
  };

  // Cabeçalho
  function toISODate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function firstDayOfCurrentMonthISO() {
    const now = new Date();
    return toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  }

  function lastDayOfCurrentMonthISO() {
    const now = new Date();
    // dia 0 do mês seguinte = último dia do mês atual
    return toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  }

  const [dataIni, setDataIni] = useState(firstDayOfCurrentMonthISO);
  const [dataFim, setDataFim] = useState(lastDayOfCurrentMonthISO);

  const [precisaRecalcular, setPrecisaRecalcular] = useState(false);
  const [loadingRecalc, setLoadingRecalc] = useState(false);

  // Nome campanha
  const [nomesOptions, setNomesOptions] = useState([]);
  const [objetivoNomeSel, setObjetivoNomeSel] = useState(null);
  const [nomeInput, setNomeInput] = useState("");

  // Dialog Nome (Adicionar/Editar)
  const [openNomeNovo, setOpenNomeNovo] = useState(false);
  const [openNomeEditar, setOpenNomeEditar] = useState(false);
  const [nomeDraft, setNomeDraft] = useState("");

  // Valores gerais do objetivo (numeric)
  const [objQtd, setObjQtd] = useState("");
  const [objEuros, setObjEuros] = useState("");
  const [objLitros, setObjLitros] = useState("");
  const [objClientes, setObjClientes] = useState("");

  // Options
  const [fabricantes, setFabricantes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [submarcas, setSubmarcas] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [embalagens, setEmbalagens] = useState([]);

  const [vendedores, setVendedores] = useState([]);

  // Equipa (por agora: 1 equipa apenas) e/ou vendedores
  const [equipaDraft, setEquipaDraft] = useState(null);
  const [individuaisDraft, setIndividuaisDraft] = useState([]);

  const [segPot, setSegPot] = useState([]);
  const [segN1, setSegN1] = useState([]);
  const [segN2, setSegN2] = useState([]);
  const [segN3, setSegN3] = useState([]);

  const [bloqFor, setBloqFor] = useState([]);
  const [bloqFat, setBloqFat] = useState([]);
  const [bloqOrd, setBloqOrd] = useState([]);

  const [gruposClientes, setGruposClientes] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [enabledAcc, setEnabledAcc] = useState({
    familias: true,
    fabricantes: false,
    marcas: false,
    submarcas: false,
    embalagens: false,
    materiais: false,
    segpot: false,
    segn1: false,
    segn2: false,
    segn3: false,
    bloqforn: false,
    bloqfat: false,
    bloqord: false,
    grupos: false,
    clientes: false,
  });

  // Selections
  const [fabricantesSel, setFabricantesSel] = useState([]);
  const [marcasSel, setMarcasSel] = useState([]);
  const [submarcasSel, setSubmarcasSel] = useState([]);
  const [familiasSel, setFamiliasSel] = useState([]);
  const [materiaisSel, setMateriaisSel] = useState([]);
  const [embalagensSel, setEmbalagensSel] = useState([]);

  const [segPotSel, setSegPotSel] = useState([]);
  const [segN1Sel, setSegN1Sel] = useState([]);
  const [segN2Sel, setSegN2Sel] = useState([]);
  const [segN3Sel, setSegN3Sel] = useState([]);

  // Bloqueios: selecionados = excluir
  const [bloqForSel, setBloqForSel] = useState([]);
  const [bloqFatSel, setBloqFatSel] = useState([]);
  const [bloqOrdSel, setBloqOrdSel] = useState([]);

  const [gruposSel, setGruposSel] = useState([]);

  // Clientes: modo todos + manual
  const [clientesModoTodos, setClientesModoTodos] = useState(true);
  const [clientesSel, setClientesSel] = useState([]);

  // Dialogs
  const [openEquipas, setOpenEquipas] = useState(false);
  const [openIndividuais, setOpenIndividuais] = useState(false);

  const carregarCascataEdicao = async (filtros = {}) => {
    // filtros = payload.filtros (o que vem da BD)
    const familiasDoObj = Array.isArray(filtros.familias) ? filtros.familias : [];
    const fabricantesDoObj = Array.isArray(filtros.fabricantes) ? filtros.fabricantes : [];
    const marcasDoObj = Array.isArray(filtros.marcas) ? filtros.marcas : [];
    const submarcasDoObj = Array.isArray(filtros.submarcas) ? filtros.submarcas : [];
    const embalagensDoObj = Array.isArray(filtros.embalagens) ? filtros.embalagens : [];
    const materiaisDoObj = Array.isArray(filtros.materiais) ? filtros.materiais : [];

    if (!familiasDoObj.length) return;

    setLoadingRecalc(true);
    setLoading(true);

    try {
      // 0) Fabricantes por famílias (preservar seleção da BD)
      const fabRes = await carregarFabricantesPorFamiliasVendas(familiasDoObj, {
        preservarSelecao: true,
        selecaoAtual: fabricantesDoObj,
      });

      const fabricantesParaQuery = fabRes.refsSelecionadas || [];
      if (!fabricantesParaQuery.length) return;

      // 1) Marcas
      const [mk, errMk] = await handleApi(
        api.post("Comercial/SelMarcasPorFamiliasFabricantesVendas", {
          familias: familiasDoObj,
          fabricantes: fabricantesParaQuery,
          term: "",
        })
      );
      if (errMk) {
        showAlert(errMk, "error");
        return;
      }

      const arrMk = Array.isArray(mk) ? mk : [];
      const mkRefs = refsFromArr(arrMk);
      setMarcas(arrMk);

      // se BD tem seleção => interseção; senão assume tudo
      const marcasSelFinal = (marcasDoObj.length ? marcasDoObj : mkRefs).filter((r) =>
        mkRefs.includes(r)
      );
      setMarcasSel(marcasSelFinal);

      // 2) Submarcas (depende de marcas selecionadas)
      const [sm, errSm] = await handleApi(
        api.post("Comercial/SelSubmarcasPorFamiliasFabricantesVendas", {
          familias: familiasDoObj,
          fabricantes: fabricantesParaQuery,
          marcas: marcasSelFinal,
          term: "",
        })
      );
      if (errSm) {
        showAlert(errSm, "error");
        return;
      }

      const arrSm = Array.isArray(sm) ? sm : [];
      const smRefs = refsFromArr(arrSm);
      setSubmarcas(arrSm);

      const submarcasSelFinal = (submarcasDoObj.length ? submarcasDoObj : smRefs).filter((r) =>
        smRefs.includes(r)
      );
      setSubmarcasSel(submarcasSelFinal);

      // 3) Embalagens
      const [emb, errEmb] = await handleApi(
        api.post("Comercial/SelEmbalagensPorFamiliasFabricantesVendas", {
          familias: familiasDoObj,
          fabricantes: fabricantesParaQuery,
          term: "",
        })
      );
      if (errEmb) {
        showAlert(errEmb, "error");
        return;
      }

      const arrEmb = Array.isArray(emb) ? emb : [];
      const embRefs = refsFromArr(arrEmb);
      setEmbalagens(arrEmb);

      const embalagensSelFinal = (embalagensDoObj.length ? embalagensDoObj : embRefs).filter((r) =>
        embRefs.includes(r)
      );
      setEmbalagensSel(embalagensSelFinal);

      // 4) Materiais
      const [mats, errMats] = await handleApi(
        api.post("Comercial/SelMateriaisPorFiltros", {
          fabricantes: fabricantesParaQuery,
          familias: familiasDoObj,
          marcas: marcasSelFinal,
          submarcas: submarcasSelFinal,
          embalagens: embalagensSelFinal,
        })
      );
      if (errMats) {
        showAlert(errMats, "error");
        return;
      }

      const arrM = Array.isArray(mats) ? mats : [];
      const matRefs = refsFromArr(arrM);
      setMateriais(arrM);

      const materiaisSelFinal = (materiaisDoObj.length ? materiaisDoObj : matRefs).filter((r) =>
        matRefs.includes(r)
      );
      setMateriaisSel(materiaisSelFinal);

      // ✅ agora está tudo coerente sem clicar “Atualizar”
      setPrecisaRecalcular(false);

      setEnabledAcc((prev) => ({
        ...prev,
        fabricantes: true,
        marcas: true,
        submarcas: true,
        embalagens: true,
        materiais: true,
        segpot: true,
        segn1: true,
        segn2: true,
        segn3: true,
        bloqforn: true,
        bloqfat: true,
        bloqord: true,
        grupos: true,
        clientes: true,
      }));
    } finally {
      setLoadingRecalc(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editando) {
      setEdicaoCarregada(true); // em criar, considera "pronto"
      return;
    }
    setEdicaoCarregada(false);
  }, [editando, idObjetivo]);

  useEffect(() => {
    if (!editando) return;

    let alive = true;

    (async () => {
      setLoading(true);
      try {
        const [payload, err] = await handleApi(
          api.post("Comercial/SelObjetivo", { id: Number(idObjetivo) })
        );

        if (!alive) return;

        if (err) {
          showAlert(err, "error");
          return;
        }

        const obj = payload?.objetivo || {};
        const f = payload?.filtros || {};

        // Cabeçalho
        setObjetivoNomeSel(
          obj?.idobjetivonome ? { id: obj.idobjetivonome, nome: obj.objetivo_nome || "" } : null
        );
        setNomeInput(obj?.objetivo_nome || "");
        setDataIni(obj?.dataini || "");
        setDataFim(obj?.datafim || "");

        // Valores fabricante
        setObjQtd(obj?.qtd ?? "");
        setObjEuros(obj?.euros ?? "");
        setObjLitros(obj?.litros ?? "");
        setObjClientes(obj?.clientes ?? "");

        // Filtros
        setFamiliasSel(Array.isArray(f.familias) ? f.familias : []);
        setFabricantesSel(Array.isArray(f.fabricantes) ? f.fabricantes : []);

        const familiasDoObj = Array.isArray(f.familias) ? f.familias : [];
        const fabricantesDoObj = Array.isArray(f.fabricantes) ? f.fabricantes : [];

        if (familiasDoObj.length > 0) {
          const fabRes = await carregarFabricantesPorFamiliasVendas(familiasDoObj, {
            preservarSelecao: true,
            selecaoAtual: fabricantesDoObj,
          });

          if (!alive) return;
          setFabricantesSel(fabRes.refsSelecionadas || []);
        }

        setMarcasSel(Array.isArray(f.marcas) ? f.marcas : []);
        setSubmarcasSel(Array.isArray(f.submarcas) ? f.submarcas : []);
        setMateriaisSel(Array.isArray(f.materiais) ? f.materiais : []);

        setSegPotSel(Array.isArray(f.segmentopotencial) ? f.segmentopotencial : []);
        setSegN1Sel(Array.isArray(f.segmenton1) ? f.segmenton1 : []);
        setSegN2Sel(Array.isArray(f.segmenton2) ? f.segmenton2 : []);
        setSegN3Sel(Array.isArray(f.segmenton3) ? f.segmenton3 : []);

        setBloqForSel(Array.isArray(f.bloq_for) ? f.bloq_for : []);
        setBloqFatSel(Array.isArray(f.bloq_fat) ? f.bloq_fat : []);
        setBloqOrdSel(Array.isArray(f.bloq_ord) ? f.bloq_ord : []);

        setGruposSel(Array.isArray(f.grupos_clientes) ? f.grupos_clientes : []);

        const todos = Number(f.todosclientes || 0) === 1;
        setClientesModoTodos(todos);
        setClientesSel(Array.isArray(f.clientes_ref) ? f.clientes_ref : []);

        setEquipaDraft(payload?.equipa || null);
        setIndividuaisDraft(Array.isArray(payload?.individuais) ? payload.individuais : []);

        setEnabledAcc((prev) => ({
          ...prev,
          familias: true,
          fabricantes: true,
          marcas: true,
          submarcas: true,
          embalagens: true,
          materiais: true,
          segpot: true,
          segn1: true,
          segn2: true,
          segn3: true,
          bloqforn: true,
          bloqfat: true,
          bloqord: true,
          grupos: true,
          clientes: true,
        }));

        await carregarCascataEdicao(f);

        setExpandedPanel(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando, idObjetivo]);

  function limparTel(nome) {
    const s = (nome ?? "").toString();
    // corta a partir de "TEL.:", "TEL:", "TL:" (case-insensitive), incluindo o que vem depois
    return s.replace(/\s*(TEL\.?:|TL:).*$/i, "").trim();
  }

  const podeGravarDestinatarios =
    (equipaDraft?.vendedores?.length || 0) > 0 || (individuaisDraft?.length || 0) > 0;

  const onPanelClosed = (panel) => {
    if (panel === "familias") {
      if (!familiasSel || familiasSel.length === 0) {
        showAlert("Seleciona pelo menos uma família.", "warning");
        setExpandedPanel("familias");
        return;
      }

      // ✅ Em edição: NÃO recalcular fabricantes automaticamente ao fechar Famílias.
      // (Só deve acontecer por ação manual — ex.: botão ATUALIZAR)
      // dentro do onPanelClosed (panel === "familias")
      if (editando) {
        return;
      }

      (async () => {
        setLoading(true);

        try {
          await carregarFabricantesPorFamiliasVendas(familiasSel);

          setEnabledAcc((prev) => ({
            ...prev,
            fabricantes: true,
          }));

          setExpandedPanel("fabricantes");
        } finally {
          setLoading(false);
        }
      })();

      return;
    }

    if (panel === "fabricantes") {
      setEnabledAcc((prev) => ({
        ...prev,
        submarcas: true,
        embalagens: true,
        materiais: true,
        segpot: true,
        segn1: true,
        segn2: true,
        segn3: true,
        bloqforn: true,
        bloqfat: true,
        bloqord: true,
        grupos: true,
        clientes: true,
      }));
    }
  };

  // Accordion control + click away
  const onAccChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);

    if (!isExpanded) {
      onPanelClosed(panel);
    }
  };

  const closeAllAccordions = () => {
    const panel = expandedPanel;
    setExpandedPanel(null);

    if (panel) {
      onPanelClosed(panel);
    }
  };

  const carregarCombosIniciais = async () => {
    setLoading(true);

    try {
      // Nota: endpoints serão criados no passo 3. Por agora a página já fica montada.
      const [nomes, errNomes] = await handleApi(
        api.post("Comercial/SelObjetivosNomesCombo", { term: "" })
      );
      if (!errNomes) setNomesOptions(Array.isArray(nomes) ? nomes : []);

      const [famsCombo, errFamsCombo] = await handleApi(api.post("Comercial/SelFamiliasCombo", {}));
      if (!errFamsCombo) {
        const arr = Array.isArray(famsCombo) ? famsCombo : [];
        setFamilias(arr);

        // ⚠️ Só aplicar default quando NÃO estamos a editar
        if (!editando) {
          const refs = arr.map((x) => toRef(x).trim());
          const def = refs.includes("001") ? ["001"] : refs.length ? [refs[0]] : [];
          setFamiliasSel(def);

          // como mudou família (ou definiu default), marcamos que falta recalcular downstream
          setPrecisaRecalcular(true);

          // opcional: abrir logo o accordion de famílias
          setExpandedPanel("familias");
        }
      }

      const [emb, errEmb] = await handleApi(api.post("Comercial/SelEmbalagensCombo", {}));
      if (errEmb) showAlert(errEmb, "error");
      else {
        const arrEmb = Array.isArray(emb) ? emb : [];
        setEmbalagens(arrEmb);

        // ✅ No criar: por defeito ficam todas selecionadas (igual aos segmentos/bloqueios/grupos)
        if (!editando) {
          setEmbalagensSel(arrEmb.map((x) => toRef(x).trim()).filter(Boolean));
        }
      }

      const [p, errP] = await handleApi(
        api.post("Comercial/SelSegmentosDistinct", { campo: "segmentopotencial" })
      );
      if (errP) showAlert(errP, "error");

      const [n1, errN1] = await handleApi(
        api.post("Comercial/SelSegmentosDistinct", { campo: "segmenton1" })
      );
      if (errN1) showAlert(errN1, "error");

      const [n2, errN2] = await handleApi(
        api.post("Comercial/SelSegmentosDistinct", { campo: "segmenton2" })
      );
      if (errN2) showAlert(errN2, "error");

      const [n3, errN3] = await handleApi(
        api.post("Comercial/SelSegmentosDistinct", { campo: "segmenton3" })
      );
      if (errN3) showAlert(errN3, "error");

      const [vends, errVends] = await handleApi(api.post("Comercial/SelVendedoresCombo", {}));
      if (errVends) showAlert(errVends, "error");
      else setVendedores(Array.isArray(vends) ? vends : []);

      const pArr = Array.isArray(p) ? p : [];
      setSegPot(pArr);
      if (!editando) setSegPotSel(pArr.map((x) => toNome(x)));

      const n1Arr = Array.isArray(n1) ? n1 : [];
      setSegN1(n1Arr);
      if (!editando) setSegN1Sel(n1Arr.map((x) => toNome(x)));

      const n2Arr = Array.isArray(n2) ? n2 : [];
      setSegN2(n2Arr);
      if (!editando) setSegN2Sel(n2Arr.map((x) => toNome(x)));

      const n3Arr = Array.isArray(n3) ? n3 : [];
      setSegN3(n3Arr);
      if (!editando) setSegN3Sel(n3Arr.map((x) => toNome(x)));

      const [bf] = await handleApi(api.post("Comercial/SelBloqueiosFornecimentoCombo", {}));
      const [bfa] = await handleApi(api.post("Comercial/SelBloqueiosFaturacaoCombo", {}));
      const [bo] = await handleApi(api.post("Comercial/SelBloqueiosOrdemCombo", {}));

      const bfArr = Array.isArray(bf) ? bf : [];
      setBloqFor(bfArr);
      if (!editando) setBloqForSel(bfArr.map((x) => toRef(x)));

      const bfaArr = Array.isArray(bfa) ? bfa : [];
      setBloqFat(bfaArr);
      if (!editando) setBloqFatSel(bfaArr.map((x) => toRef(x)));

      const boArr = Array.isArray(bo) ? bo : [];
      setBloqOrd(boArr);
      if (!editando) setBloqOrdSel(boArr.map((x) => toRef(x)));

      const [gc] = await handleApi(api.post("Comercial/SelEntidadesGruposCombo", {}));
      const gcArr = Array.isArray(gc) ? gc : [];
      setGruposClientes(gcArr);
      if (!editando) setGruposSel(gcArr.map((x) => toRef(x)));
    } catch (e) {
      showAlert(e?.message || e?.toString?.() || "Erro a carregar combos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const reloadNomesOptions = async (term = "") => {
    const [nomes, errNomes] = await handleApi(
      api.post("Comercial/SelObjetivosNomesCombo", { term })
    );
    if (errNomes) {
      showAlert(errNomes, "error");
      return [];
    }
    const arr = Array.isArray(nomes) ? nomes : [];
    setNomesOptions(arr);
    return arr;
  };

  const abrirNovoNome = () => {
    setNomeDraft(nomeInput || "");
    setOpenNomeNovo(true);
  };

  const abrirEditarNome = () => {
    if (!objetivoNomeSel) return;

    setNomeDraft(objetivoNomeSel?.nome ?? objetivoNomeSel?.Nome ?? "");

    setOpenNomeEditar(true);
  };

  const guardarNovoNome = async () => {
    const nome = (nomeDraft || "").trim();
    if (!nome) {
      showAlert("Indica o nome.", "warning");
      return;
    }

    setLoading(true);
    try {
      const [novo, err, meta] = await handleApi(api.post("Comercial/InsObjetivoNome", { nome }));

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (meta?.message) {
        showAlert(meta.message, meta.severity || "success");
      }

      // ✅ Agora a API devolve data = { id, nome }
      await reloadNomesOptions("");

      if (novo?.id) {
        setObjetivoNomeSel(novo);
        setNomeInput(novo.nome || "");
      }

      setOpenNomeNovo(false);
    } finally {
      setLoading(false);
    }
  };

  const guardarEditarNome = async () => {
    if (!objetivoNomeSel) return;

    const id = Number(objetivoNomeSel?.id ?? 0);
    const nome = (nomeDraft || "").trim();

    if (id <= 0) {
      showAlert("Nome selecionado inválido.", "error");
      return;
    }
    if (!nome) {
      showAlert("Indica o nome.", "warning");
      return;
    }

    setLoading(true);
    try {
      const [_ok, err, meta] = await handleApi(api.post("Comercial/UpdObjetivoNome", { id, nome }));

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (meta?.message) {
        showAlert(meta.message, meta.severity || "success");
      }

      const arr = await reloadNomesOptions("");

      // re-selecionar o registo editado (para atualizar o label)
      const atualizado = arr.find((x) => Number(x?.id ?? x?.Id) === id) || { id, nome };

      setObjetivoNomeSel(atualizado);
      setNomeInput(atualizado?.nome || nome);

      setOpenNomeEditar(false);
    } finally {
      setLoading(false);
    }
  };

  const carregarFabricantesPorFamiliasVendas = async (familiasRefs, opts = {}) => {
    const { preservarSelecao = false, selecaoAtual = [] } = opts;

    if (!familiasRefs || familiasRefs.length === 0) {
      setFabricantes([]);
      setFabricantesSel([]);
      return { arr: [], refsValidas: [], refsSelecionadas: [] };
    }

    setLoading(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelFabricantesPorFamiliasVendas", { familias: familiasRefs })
      );
      if (err) {
        showAlert(err, "error");
        return { arr: [], refsValidas: [], refsSelecionadas: [] };
      }

      const arr = Array.isArray(payload) ? payload : [];
      const refsValidas = arr.map((x) => toRef(x).trim()).filter(Boolean);

      let refsSelecionadas = [];
      if (preservarSelecao) {
        refsSelecionadas = (Array.isArray(selecaoAtual) ? selecaoAtual : []).filter((r) =>
          refsValidas.includes(r)
        );
      } else {
        refsSelecionadas = refsValidas;
      }

      // ✅ Atualiza UI
      setFabricantes(arr);
      setFabricantesSel(refsSelecionadas);

      return { arr, refsValidas, refsSelecionadas };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCombosIniciais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Em edição NÃO fazemos auto-load/reset a jusante
    if (editando) return;

    carregarFabricantesPorFamiliasVendas(familiasSel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando, JSON.stringify(familiasSel)]);

  // Clientes (por filtros)
  useEffect(() => {
    const carregarClientes = async () => {
      if (precisaRecalcular) return;

      if (!gruposSel || gruposSel.length === 0) {
        setClientes([]);
        if (!clientesModoTodos) setClientesSel([]);
        return;
      }

      setLoading(true);

      try {
        const body = {
          refgrupos: gruposSel,
          segmentopotencial: segPotSel,
          segmenton1: segN1Sel,
          segmenton2: segN2Sel,
          segmenton3: segN3Sel,
          bloqFor: bloqForSel,
          bloqFat: bloqFatSel,
          bloqOrd: bloqOrdSel,
          term: "",
        };
        const [cli, err] = await handleApi(
          api.post("Comercial/SelClientesPorFiltrosObjetivo", body)
        );
        if (err) {
          showAlert(err, "error");
          setClientes([]);
          if (!clientesModoTodos) setClientesSel([]);
          return;
        }
        const arr = Array.isArray(cli) ? cli : [];

        // console.log("cli", cli);
        // console.log("arr", arr);

        setClientes(arr);

        if (!clientesModoTodos) {
          setClientesSel((prev) => (prev && prev.length > 0 ? prev : arr.map((x) => toRef(x))));
        }
      } finally {
        setLoading(false);
      }
    };

    carregarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientesModoTodos,
    precisaRecalcular,
    JSON.stringify(gruposSel),
    JSON.stringify(segPotSel),
    JSON.stringify(segN1Sel),
    JSON.stringify(segN2Sel),
    JSON.stringify(segN3Sel),
    JSON.stringify(bloqForSel),
    JSON.stringify(bloqFatSel),
    JSON.stringify(bloqOrdSel),
  ]);

  // options normalizadas p/ componentes
  const fabricantesOpt = useMemo(
    () => fabricantes.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [fabricantes]
  );
  const marcasOpt = useMemo(
    () => marcas.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [marcas]
  );

  const submarcasOpt = useMemo(
    () => submarcas.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [submarcas]
  );
  const familiasOpt = useMemo(
    () => familias.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [familias]
  );
  const embalagensOpt = useMemo(
    () => embalagens.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [embalagens]
  );

  const materiaisOpt = useMemo(
    () => materiais.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [materiais]
  );

  const vendedoresOpt = useMemo(
    () =>
      vendedores.map((x) => ({
        value: toRef(x),
        label: limparTel(toNome(x)),
      })),
    [vendedores]
  );

  const segPotOpt = useMemo(
    () => segPot.map((x) => ({ value: toNome(x), label: toNome(x) })),
    [segPot]
  );
  const segN1Opt = useMemo(
    () => segN1.map((x) => ({ value: toNome(x), label: toNome(x) })),
    [segN1]
  );
  const segN2Opt = useMemo(
    () => segN2.map((x) => ({ value: toNome(x), label: toNome(x) })),
    [segN2]
  );
  const segN3Opt = useMemo(
    () => segN3.map((x) => ({ value: toNome(x), label: toNome(x) })),
    [segN3]
  );

  const bloqForOpt = useMemo(
    () => bloqFor.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [bloqFor]
  );
  const bloqFatOpt = useMemo(
    () => bloqFat.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [bloqFat]
  );
  const bloqOrdOpt = useMemo(
    () => bloqOrd.map((x) => ({ value: toRef(x), label: toNome(x) })),
    [bloqOrd]
  );

  const gruposOpt = useMemo(
    () => gruposClientes.map((x) => ({ value: toRef(x), label: `${toNome(x)} (${toRef(x)})` })),
    [gruposClientes]
  );

  const clientesOpt = useMemo(
    () =>
      clientes.map((x) => ({
        value: toRef(x),
        // clientes já vêm do backend como { value, label } — respeitar o label (nome)
        label: (x?.label ?? "").toString() || `${toNome(x)} (${toRef(x)})`,
      })),
    [clientes]
  );

  const gravar = async () => {
    if (!podeGerir) return;

    if (!familiasSel || familiasSel.length === 0) {
      showAlert("Seleciona pelo menos uma família.", "warning");
      setExpandedPanel("familias");
      return;
    }

    if (!fabricantesSel || fabricantesSel.length === 0) {
      showAlert("Seleciona pelo menos um fabricante.", "warning");
      setExpandedPanel("fabricantes");
      return;
    }

    if (!objetivoNomeSel?.id) return showAlert("Seleciona o nome da campanha.", "warning");
    if (!dataIni || !dataFim) return showAlert("Preenche a data inicial e final.", "warning");

    const numOrNull = (v) => {
      const s = (v ?? "").toString().trim();
      if (!s) return null;
      const n = Number(s.replace(",", "."));
      if (Number.isNaN(n)) return null;
      return n;
    };

    const qtd = numOrNull(objQtd);
    const euros = numOrNull(objEuros);
    const litros = numOrNull(objLitros);
    const clientesVal = numOrNull(objClientes);

    const temAlgumValorGeral =
      (qtd ?? 0) > 0 || (euros ?? 0) > 0 || (litros ?? 0) > 0 || (clientesVal ?? 0) > 0;

    // if (!temAlgumValorGeral) {
    //   return showAlert(
    //     "Define pelo menos um valor geral do objetivo (QTD/Euros/Litros/Clientes).",
    //     "warning"
    //   );
    // }

    const equipaTemVendedores = !!equipaDraft?.vendedores?.length;
    const temIndividuais = (individuaisDraft?.length || 0) > 0;

    if (!equipaTemVendedores && !temIndividuais) {
      showAlert("Define pelo menos uma Equipa com vendedores ou Objetivos Individuais.", "warning");
      setOpenEquipas(true);
      return;
    }

    const body = {
      idobjetivonome: objetivoNomeSel.id,
      dataini: dataIni,
      datafim: dataFim,

      qtd: qtd,
      euros: euros,
      litros: litros,
      clientes: clientesVal,

      fabricantes: fabricantesSel,
      marcas: marcasSel,
      submarcas: submarcasSel,
      familias: familiasSel,
      embalagens: embalagensSel,
      materiais: materiaisSel,

      segmentopotencial: segPotSel,
      segmenton1: segN1Sel,
      segmenton2: segN2Sel,
      segmenton3: segN3Sel,

      bloq_for: bloqForSel,
      bloq_fat: bloqFatSel,
      bloq_ord: bloqOrdSel,

      grupos_clientes: gruposSel,

      todosclientes: clientesModoTodos ? 1 : 0,
      clientes_ref: clientesModoTodos ? [] : clientesSel,

      equipa: equipaDraft, // pode ser null
      individuais: individuaisDraft, // pode ser []
    };

    // escolher endpoint + incluir id em edição
    const endpoint = editando ? "Comercial/UpdObjetivo" : "Comercial/InsObjetivo";
    const bodyFinal = editando ? { ...body, id: Number(idObjetivo) } : body;

    setLoading(true);

    try {
      const [payload, err, meta] = await handleApi(api.post(endpoint, bodyFinal));
      if (err) {
        showAlert(err, "error");
        return;
      }

      // Mantém o teu padrão de mensagens
      if (meta?.message) showAlert(meta.message, meta.severity || "success");
      else showAlert(editando ? "Objetivo atualizado." : "Objetivo gravado.", "success");

      // ✅ Isto é essencial para o Dialog fechar + refresh na listagem
      if (typeof onSaved === "function") onSaved(payload);
    } finally {
      setLoading(false);
    }
  };

  const toNum0 = (v) => {
    const s = (v ?? "").toString().trim();
    if (!s) return 0;
    const n = Number(s.replace(",", "."));
    if (Number.isNaN(n)) return 0;
    return n;
  };

  const somarIndividuais = (arr, campo) => {
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    return arr.reduce((acc, it) => acc + (it && it[campo] != null ? Number(it[campo]) : 0), 0);
  };

  const calcularAvisoCobertura = () => {
    // valores do fabricante (inputs)
    const fab = {
      qtd: toNum0(objQtd),
      euros: toNum0(objEuros),
      litros: toNum0(objLitros),
      clientes: toNum0(objClientes),
    };

    // valores da equipa (draft) — podem estar null/undefined
    const eq = {
      qtd: equipaDraft?.qtd != null ? Number(equipaDraft.qtd) : 0,
      euros: equipaDraft?.euros != null ? Number(equipaDraft.euros) : 0,
      litros: equipaDraft?.litros != null ? Number(equipaDraft.litros) : 0,
      clientes: equipaDraft?.clientes != null ? Number(equipaDraft.clientes) : 0,
    };

    // soma individuais
    const ind = {
      qtd: somarIndividuais(individuaisDraft, "qtd"),
      euros: somarIndividuais(individuaisDraft, "euros"),
      litros: somarIndividuais(individuaisDraft, "litros"),
      clientes: somarIndividuais(individuaisDraft, "clientes"),
    };

    const total = {
      qtd: eq.qtd + ind.qtd,
      euros: eq.euros + ind.euros,
      litros: eq.litros + ind.litros,
      clientes: eq.clientes + ind.clientes,
    };

    const faltas = [];

    const pushSeFalta = (campo, label) => {
      if (fab[campo] > 0 && total[campo] + 1e-9 < fab[campo]) {
        faltas.push({
          campo,
          label,
          fab: fab[campo],
          total: total[campo],
          falta: fab[campo] - total[campo],
        });
      }
    };

    pushSeFalta("qtd", "QTD");
    pushSeFalta("euros", "Euros");
    pushSeFalta("litros", "Litros");
    pushSeFalta("clientes", "Clientes");

    return { faltas, fab, total };
  };

  const resetForm = () => {
    // Cabeçalho
    setObjetivoNomeSel(null);
    setNomeInput("");
    setDataIni("");
    setDataFim("");

    // Valores do fabricante
    setObjQtd("");
    setObjEuros("");
    setObjLitros("");
    setObjClientes("");

    // Seleções (filtros)
    setFamiliasSel([]);
    setFabricantesSel([]);
    setMarcasSel([]);
    setSubmarcasSel([]);
    setEmbalagensSel([]);
    setMateriaisSel([]);

    setSegPotSel([]);
    setSegN1Sel([]);
    setSegN2Sel([]);
    setSegN3Sel([]);

    setBloqForSel([]);
    setBloqFatSel([]);
    setBloqOrdSel([]);

    setGruposSel([]);

    // Clientes
    setClientesModoTodos(true);
    setClientesSel([]);

    // Equipa e Individuais
    setEquipaDraft(null);
    setIndividuaisDraft([]);

    // Accordions
    setExpandedPanel("familias");

    // Se tens enabledAcc no teu ficheiro:
    setEnabledAcc({
      familias: true,
      fabricantes: false,
      marcas: false,
      submarcas: false,
      embalagens: false,
      materiais: false,
      segpot: false,
      segn1: false,
      segn2: false,
      segn3: false,
      bloqforn: false,
      bloqfat: false,
      bloqord: false,
      grupos: false,
      clientes: false,
    });
  };

  const carregarParaEdicao = async (id) => {
    setLoading(true);

    try {
      const [payload, err] = await handleApi(api.post("Comercial/SelObjetivo", { id: Number(id) }));
      if (err) {
        showAlert(err, "error");
        return false;
      }

      const obj = payload?.objetivo || {};
      const f = payload?.filtros || {};

      // Cabeçalho
      if (obj?.idobjetivonome) {
        setObjetivoNomeSel({ id: obj.idobjetivonome, nome: obj.objetivo_nome || "" });
        setNomeInput(obj.objetivo_nome || "");
      } else {
        setObjetivoNomeSel(null);
        setNomeInput("");
      }

      setDataIni(obj?.dataini || "");
      setDataFim(obj?.datafim || "");

      // Valores fabricante
      setObjQtd(obj?.qtd ?? "");
      setObjEuros(obj?.euros ?? "");
      setObjLitros(obj?.litros ?? "");
      setObjClientes(obj?.clientes ?? "");

      // Filtros
      const normRefs = (arr) =>
        Array.isArray(arr)
          ? arr.map((x) => (typeof x === "string" ? x : toRef(x))).filter((x) => x)
          : [];
      setFamiliasSel(normRefs(f.familias));
      setFabricantesSel(normRefs(f.fabricantes));
      setMarcasSel(normRefs(f.marcas));
      setSubmarcasSel(normRefs(f.submarcas));
      setEmbalagensSel(normRefs(f.embalagens));
      setMateriaisSel(normRefs(f.materiais));

      setSegPotSel(Array.isArray(f.segmentopotencial) ? f.segmentopotencial : []);
      setSegN1Sel(Array.isArray(f.segmenton1) ? f.segmenton1 : []);
      setSegN2Sel(Array.isArray(f.segmenton2) ? f.segmenton2 : []);
      setSegN3Sel(Array.isArray(f.segmenton3) ? f.segmenton3 : []);

      setBloqForSel(Array.isArray(f.bloq_for) ? f.bloq_for : []);
      setBloqFatSel(Array.isArray(f.bloq_fat) ? f.bloq_fat : []);
      setBloqOrdSel(Array.isArray(f.bloq_ord) ? f.bloq_ord : []);

      setGruposSel(Array.isArray(f.grupos_clientes) ? f.grupos_clientes : []);

      // Clientes
      const todos = Number(f.todosclientes || 0) === 1;
      setClientesModoTodos(todos);
      setClientesSel(Array.isArray(f.clientes_ref) ? f.clientes_ref : []);

      // Equipa / Individuais
      setEquipaDraft(payload?.equipa || null);
      setIndividuaisDraft(Array.isArray(payload?.individuais) ? payload.individuais : []);

      // Accordions: em edição, deixamos tudo habilitado
      setEnabledAcc({
        familias: true,
        fabricantes: true,
        marcas: true,
        submarcas: true,
        embalagens: true,
        materiais: true,
        segpot: true,
        segn1: true,
        segn2: true,
        segn3: true,
        bloqforn: true,
        bloqfat: true,
        bloqord: true,
        grupos: true,
        clientes: true,
      });

      setExpandedPanel(null);

      setEdicaoCarregada(true);

      return true;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editando) {
      resetForm();
      return;
    }

    if (!idObjetivo) return;

    carregarParaEdicao(idObjetivo);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando, idObjetivo]);

  const { faltas } = calcularAvisoCobertura();

  const avisoCoberturaTexto =
    faltas.length === 0
      ? ""
      : `⚠️ Total definido para vendedores (Equipa + Individuais) é inferior ao objetivo do fabricante em: ${faltas
          .map((f) => f.label)
          .join(", ")}.`;

  const tooltipCobertura =
    !faltas || faltas.length === 0
      ? ""
      : faltas
          .map(
            (f) =>
              `${f.label}: fabricante ${f.fab.toFixed(2)} | vendedores ${f.total.toFixed(
                2
              )} | falta ${f.falta.toFixed(2)}`
          )
          .join(" • ");

  const content = (
    <MDBox mt={2}>
      {(loading || loadingRecalc) && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
          <CircularProgress size={22} />
          <MDTypography variant="button" sx={{ fontSize: 12, opacity: 0.8 }}>
            A atualizar filtros...
          </MDTypography>
        </MDBox>
      )}

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                  color="info"
                  startIcon={<PersonIcon />}
                  onClick={() => {
                    if (editando && (loading || !edicaoCarregada)) {
                      showAlert("A carregar dados do objetivo…", "info");
                    }
                    setOpenIndividuais(true);
                  }}
                >
                  Objetivos Individuais
                  {(individuaisDraft?.length || 0) > 0 ? ` (${individuaisDraft.length})` : ""}
                </MDButton>

                <MDButton
                  color="info"
                  startIcon={<GroupsIcon />}
                  onClick={() => setOpenEquipas(true)}
                >
                  Gerir Equipa
                  {equipaDraft?.vendedores?.length ? ` (${equipaDraft.vendedores.length})` : ""}
                </MDButton>

                {avisoCoberturaTexto && (
                  <Tooltip title={tooltipCobertura} arrow placement="top">
                    <MDBox
                      onClick={() => setOpenAvisoDetalhe(true)}
                      sx={{
                        ml: 2,
                        mr: 2,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        cursor: "pointer",
                        alignSelf: "center",
                        userSelect: "none",
                        "&:hover": { opacity: 0.9 },
                      }}
                    >
                      <MDTypography
                        variant="button"
                        sx={{
                          fontSize: 12,
                          color: "#b26a00",
                          whiteSpace: "normal",
                          maxWidth: 520,
                        }}
                      >
                        {avisoCoberturaTexto}
                      </MDTypography>
                    </MDBox>
                  </Tooltip>
                )}

                <MDButton
                  color="success"
                  disabled={!podeGerir || !podeGravarDestinatarios}
                  onClick={gravar}
                >
                  Gravar
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Linha superior */}
            <MDBox mt={2} display="flex" flexWrap="wrap" gap={2}>
              <MDBox sx={{ minWidth: 320, flex: "1 1 320px" }}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                  Nome da campanha
                </MDTypography>

                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDBox flex={1}>
                    <Autocomplete
                      options={nomesOptions}
                      value={objetivoNomeSel}
                      inputValue={nomeInput}
                      onInputChange={async (_e, v) => {
                        setNomeInput(v);
                        await reloadNomesOptions(v || "");
                      }}
                      onChange={(_e, v) => setObjetivoNomeSel(v)}
                      getOptionLabel={(o) => o?.nome ?? o?.Nome ?? ""}
                      isOptionEqualToValue={(a, b) => (a?.id ?? a?.Id) === (b?.id ?? b?.Id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Escreve para filtrar..."
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                        />
                      )}
                    />
                  </MDBox>

                  <Tooltip title="Adicionar nome">
                    <span>
                      <IconButton size="small" onClick={abrirNovoNome} sx={{ mt: 0.2 }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Editar nome selecionado">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!objetivoNomeSel}
                        onClick={abrirEditarNome}
                        sx={{ mt: 0.2 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </MDBox>
              </MDBox>

              <MDBox sx={{ minWidth: 180 }}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                  Data inicial
                </MDTypography>
                <TextField
                  type="date"
                  value={dataIni}
                  onChange={(e) => setDataIni(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                />
              </MDBox>

              <MDBox sx={{ minWidth: 180 }}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                  Data final
                </MDTypography>
                <TextField
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                />
              </MDBox>

              <MDBox sx={{ minWidth: 420, flex: "1 1 420px" }}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                  Valores estabelecidos pelo fabricante
                </MDTypography>

                <MDBox display="flex" flexWrap="wrap" gap={1}>
                  <TextField
                    label=""
                    placeholder="QTD"
                    type="number"
                    value={objQtd}
                    onChange={(e) => setObjQtd(e.target.value)}
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      width: 95,
                      "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                    }}
                  />
                  <TextField
                    label=""
                    placeholder="Euros"
                    type="number"
                    value={objEuros}
                    onChange={(e) => setObjEuros(e.target.value)}
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      width: 110,
                      "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                    }}
                  />
                  <TextField
                    label=""
                    placeholder="Litros"
                    type="number"
                    value={objLitros}
                    onChange={(e) => setObjLitros(e.target.value)}
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      width: 110,
                      "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                    }}
                  />
                  <TextField
                    label=""
                    placeholder="Clientes"
                    type="number"
                    value={objClientes}
                    onChange={(e) => setObjClientes(e.target.value)}
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      width: 110,
                      "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                    }}
                  />
                </MDBox>

                {/* <MDTypography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.75 }}>
                  Preenche pelo menos um valor (&gt; 0). Valores vazios não contam.
                </MDTypography> */}
              </MDBox>
            </MDBox>

            {/* Accordions */}
            <ClickAwayListener onClickAway={closeAllAccordions}>
              <MDBox mt={2}>
                <CriarObjetivosAccMulti
                  panelKey="familias"
                  disabled={!enabledAcc.familias}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Famílias de materiais"
                  options={familiasOpt}
                  selected={familiasSel}
                  setSelected={(vals) => {
                    setFamiliasSel(vals);
                    setPrecisaRecalcular(true);

                    // NÃO mexe em fabricantes automaticamente em edição:
                    // só no botão Atualizar.
                    if (!editando) {
                      // em criar manténs o comportamento atual (vai carregar fabricantes ao fechar)
                    }
                  }}
                  minWidthItem={260}
                  hint={
                    familiasSel.length === 0
                      ? "Sem famílias selecionadas não são apresentados materiais."
                      : null
                  }
                />

                <CriarObjetivosAccMulti
                  panelKey="fabricantes"
                  disabled={!enabledAcc.fabricantes || loadingRecalc || loading}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Fabricantes"
                  options={fabricantesOpt}
                  selected={fabricantesSel}
                  setSelected={(vals) => {
                    setFabricantesSel(vals);
                    setMarcas([]);
                    setMarcasSel([]);
                    setSubmarcas([]);
                    setSubmarcasSel([]);
                    setEmbalagens([]);
                    setEmbalagensSel([]);
                    setMateriais([]);
                    setMateriaisSel([]);
                    setPrecisaRecalcular(true);
                  }}
                  minWidthItem={220}
                />

                <MDBox mt={1} mb={2} display="flex" alignItems="center" gap={1}>
                  <MDButton
                    color={precisaRecalcular ? "warning" : "info"}
                    disabled={
                      loadingRecalc ||
                      !familiasSel?.length ||
                      !fabricantesSel?.length ||
                      !fabricantes?.length
                    }
                    onClick={async () => {
                      setExpandedPanel(null);
                      setLoadingRecalc(true);
                      setLoading(true);

                      // snapshots (para decisões determinísticas)
                      const fabricantesPrev = Array.isArray(fabricantesSel) ? fabricantesSel : [];
                      const marcasPrev = Array.isArray(marcasSel) ? marcasSel : [];
                      const submarcasPrev = Array.isArray(submarcasSel) ? submarcasSel : [];
                      const embalagensPrev = Array.isArray(embalagensSel) ? embalagensSel : [];
                      const materiaisPrev = Array.isArray(materiaisSel) ? materiaisSel : [];

                      // "all values" ANTES do refresh (para perceber se o user mexeu)
                      const fabAllPrev = refsFromArr(fabricantes);
                      const mkAllPrev = refsFromArr(marcas);
                      const smAllPrev = refsFromArr(submarcas);
                      const embAllPrev = refsFromArr(embalagens);
                      const matAllPrev = refsFromArr(materiais);

                      try {
                        // ============================================================
                        // 0) Recalcular FABRICANTES (famílias)
                        // - se estava tudo selecionado => selecionar todos
                        // - se estava parcial/vazio => interseção
                        // ============================================================
                        const fabPreservar = !(
                          fabAllPrev.length === 0 || fabricantesPrev.length === fabAllPrev.length
                        );

                        const fabRes = await carregarFabricantesPorFamiliasVendas(familiasSel, {
                          preservarSelecao: fabPreservar,
                          selecaoAtual: fabricantesPrev,
                        });

                        const fabricantesParaQuery = fabRes.refsSelecionadas;

                        if (!fabricantesParaQuery?.length) {
                          showAlert(
                            "Após atualização das famílias, não existem fabricantes selecionados válidos.",
                            "warning"
                          );
                          return;
                        }

                        // ============================================================
                        // 1.1) Marcas por vendas (famílias + fabricantes)
                        // ============================================================
                        const bodyMk = {
                          familias: familiasSel,
                          fabricantes: fabricantesParaQuery,
                          term: "",
                        };

                        const [mk, errMk] = await handleApi(
                          api.post("Comercial/SelMarcasPorFamiliasFabricantesVendas", bodyMk)
                        );
                        if (errMk) {
                          showAlert(errMk, "error");
                          return;
                        }

                        const arrMk = Array.isArray(mk) ? mk : [];
                        const mkRefs = refsFromArr(arrMk);
                        setMarcas(arrMk);

                        const marcasSelecionadas = nextSelectionByAllState(
                          marcasPrev,
                          mkAllPrev,
                          mkRefs
                        );
                        setMarcasSel(marcasSelecionadas);

                        // ============================================================
                        // 1.2) Submarcas por vendas (famílias + fabricantes)
                        // ============================================================
                        const bodySm = {
                          familias: familiasSel,
                          fabricantes: fabricantesParaQuery,
                          marcas: marcasSelecionadas,
                          term: "",
                        };

                        const [sm, errSm] = await handleApi(
                          api.post("Comercial/SelSubmarcasPorFamiliasFabricantesVendas", bodySm)
                        );
                        if (errSm) {
                          showAlert(errSm, "error");
                          return;
                        }

                        const arrSm = Array.isArray(sm) ? sm : [];
                        const smRefs = refsFromArr(arrSm);
                        setSubmarcas(arrSm);

                        const submarcasSelecionadas = nextSelectionByAllState(
                          submarcasPrev,
                          smAllPrev,
                          smRefs
                        );
                        setSubmarcasSel(submarcasSelecionadas);

                        // ============================================================
                        // 1.5) Embalagens por vendas (famílias + fabricantes)
                        // ============================================================
                        const bodyEmb = {
                          familias: familiasSel,
                          fabricantes: fabricantesParaQuery,
                          term: "",
                        };

                        const [emb, errEmb] = await handleApi(
                          api.post("Comercial/SelEmbalagensPorFamiliasFabricantesVendas", bodyEmb)
                        );
                        if (errEmb) {
                          showAlert(errEmb, "error");
                          return;
                        }

                        const arrEmb = Array.isArray(emb) ? emb : [];
                        const embRefs = refsFromArr(arrEmb);
                        setEmbalagens(arrEmb);

                        const embalagensSelecionadas = nextSelectionByAllState(
                          embalagensPrev,
                          embAllPrev,
                          embRefs
                        );
                        setEmbalagensSel(embalagensSelecionadas);

                        // ============================================================
                        // 2) Materiais (usar submarcas SELECIONADAS, não "todas")
                        // ============================================================
                        const bodyMats = {
                          fabricantes: fabricantesParaQuery,
                          familias: familiasSel,
                          marcas: marcasSelecionadas,
                          submarcas: submarcasSelecionadas,
                          embalagens: embalagensSelecionadas,
                        };

                        const [mats, errMats] = await handleApi(
                          api.post("Comercial/SelMateriaisPorFiltros", bodyMats)
                        );
                        if (errMats) {
                          showAlert(errMats, "error");
                          return;
                        }

                        const arrM = Array.isArray(mats) ? mats : [];
                        const matRefs = refsFromArr(arrM);

                        setMateriais(arrM);

                        // console.log("Materiais carregados:", arrM.length, {
                        //   familias: familiasSel?.length,
                        //   fabricantes: fabricantesParaQuery?.length,
                        //   submarcas: submarcasSelecionadas?.length,
                        //   embalagens: embalagensSel?.length,
                        // });

                        const materiaisSelecionados = nextSelectionByAllState(
                          materiaisPrev,
                          matAllPrev,
                          matRefs
                        );
                        setMateriaisSel(materiaisSelecionados);

                        setPrecisaRecalcular(false);
                      } finally {
                        setLoadingRecalc(false);
                        setLoading(false);
                      }

                      // ============================================================
                      // 3) Habilitar accordions a jusante e abrir Submarcas
                      // ============================================================
                      setEnabledAcc((prev) => ({
                        ...prev,
                        fabricantes: true,
                        marcas: true,
                        submarcas: true,
                        embalagens: true,
                        materiais: true,
                        segpot: true,
                        segn1: true,
                        segn2: true,
                        segn3: true,
                        bloqforn: true,
                        bloqfat: true,
                        bloqord: true,
                        grupos: true,
                        clientes: true,
                      }));

                      setExpandedPanel(null);
                    }}
                  >
                    Atualizar
                  </MDButton>

                  {loading && (
                    <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
                      <CircularProgress />
                    </MDBox>
                  )}

                  {precisaRecalcular && (
                    <MDTypography variant="button" color="warning">
                      Alterações pendentes — clica em Atualizar.
                    </MDTypography>
                  )}
                </MDBox>

                <CriarObjetivosAccMulti
                  panelKey="marcas"
                  disabled={!enabledAcc.marcas || loadingRecalc}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Marcas"
                  options={marcasOpt}
                  selected={marcasSel}
                  setSelected={(vals) => {
                    setMarcasSel(vals);

                    // Submarcas dependem de Marcas => evita lista “antiga”
                    setSubmarcas([]);
                    setSubmarcasSel([]);

                    // Materiais dependem (em cascata)
                    setMateriais([]);
                    setMateriaisSel([]);

                    setPrecisaRecalcular(true);
                  }}
                  minWidthItem={220}
                  hint={
                    precisaRecalcular
                      ? "Alterações pendentes — clica em Atualizar para recalcular."
                      : null
                  }
                />

                <CriarObjetivosAccMulti
                  panelKey="submarcas"
                  disabled={!enabledAcc.submarcas || loadingRecalc}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Submarcas"
                  options={submarcasOpt}
                  selected={submarcasSel}
                  setSelected={(vals) => {
                    setSubmarcasSel(vals);

                    // Materiais dependem de Submarcas/Embalagens => evita lista “antiga”
                    setMateriais([]);
                    setMateriaisSel([]);

                    setPrecisaRecalcular(true);
                  }}
                  minWidthItem={220}
                  hint={
                    precisaRecalcular
                      ? "Alterações pendentes — clica em Atualizar para recalcular."
                      : null
                  }
                />

                <CriarObjetivosAccMulti
                  panelKey="embalagens"
                  disabled={!enabledAcc.embalagens || loadingRecalc}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Embalagens"
                  options={embalagensOpt}
                  selected={embalagensSel}
                  setSelected={(vals) => {
                    setEmbalagensSel(vals);

                    // Materiais dependem de Submarcas/Embalagens => evita lista “antiga”
                    setMateriais([]);
                    setMateriaisSel([]);

                    setPrecisaRecalcular(true);
                  }}
                  minWidthItem={220}
                  hint={
                    precisaRecalcular
                      ? "Alterações pendentes — clica em Atualizar para recalcular."
                      : null
                  }
                />

                <CriarObjetivosAccMulti
                  panelKey="materiais"
                  disabled={!enabledAcc.materiais || precisaRecalcular || loadingRecalc}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Materiais"
                  options={materiaisOpt}
                  selected={materiaisSel}
                  setSelected={setMateriaisSel}
                  minWidthItem={320}
                  emptyHint={
                    precisaRecalcular
                      ? "Alterações pendentes — clica em Atualizar para recalcular os materiais."
                      : "Seleciona famílias para carregar materiais."
                  }
                  hint={
                    precisaRecalcular
                      ? "A lista de materiais só é recalculada após Atualizar (para refletir Submarcas/Embalagens)."
                      : null
                  }
                />

                <CriarObjetivosAccMulti
                  panelKey="segpot"
                  disabled={!enabledAcc.segpot}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Segmento Potencial"
                  options={segPotOpt}
                  selected={segPotSel}
                  setSelected={setSegPotSel}
                />
                <CriarObjetivosAccMulti
                  panelKey="segn1"
                  disabled={!enabledAcc.segn1}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Segmento N1"
                  options={segN1Opt}
                  selected={segN1Sel}
                  setSelected={setSegN1Sel}
                />
                <CriarObjetivosAccMulti
                  panelKey="segn2"
                  disabled={!enabledAcc.segn2}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Segmento N2"
                  options={segN2Opt}
                  selected={segN2Sel}
                  setSelected={setSegN2Sel}
                />
                <CriarObjetivosAccMulti
                  panelKey="segn3"
                  disabled={!enabledAcc.segn3}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Segmento N3"
                  options={segN3Opt}
                  selected={segN3Sel}
                  setSelected={setSegN3Sel}
                />

                <CriarObjetivosAccMulti
                  panelKey="bloqfor"
                  disabled={!enabledAcc.bloqforn}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Bloqueios de Fornecimento (exclusão de clientes)"
                  options={bloqForOpt}
                  selected={bloqForSel}
                  setSelected={setBloqForSel}
                  minWidthItem={260}
                  hint="Clientes com qualquer bloqueio selecionado ficam excluídos do apuramento."
                />
                <CriarObjetivosAccMulti
                  panelKey="bloqfat"
                  disabled={!enabledAcc.bloqfat}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Bloqueios de Faturação (exclusão de clientes)"
                  options={bloqFatOpt}
                  selected={bloqFatSel}
                  setSelected={setBloqFatSel}
                  minWidthItem={260}
                />
                <CriarObjetivosAccMulti
                  panelKey="bloqord"
                  disabled={!enabledAcc.bloqord}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Bloqueios de Ordem (exclusão de clientes)"
                  options={bloqOrdOpt}
                  selected={bloqOrdSel}
                  setSelected={setBloqOrdSel}
                  minWidthItem={260}
                />

                <CriarObjetivosAccMulti
                  panelKey="grupos"
                  disabled={!enabledAcc.grupos}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  title="Grupos de Clientes"
                  options={gruposOpt}
                  selected={gruposSel}
                  setSelected={setGruposSel}
                  minWidthItem={320}
                />

                <CriarObjetivosAccClientes
                  panelKey="clientes"
                  disabled={!enabledAcc.clientes || precisaRecalcular || loadingRecalc}
                  expandedPanel={expandedPanel}
                  onChange={onAccChange}
                  clientesOptions={clientesOpt}
                  clientesModoTodos={clientesModoTodos}
                  setClientesModoTodos={setClientesModoTodos}
                  clientesSel={clientesSel}
                  setClientesSel={setClientesSel}
                  hint={
                    precisaRecalcular
                      ? "Alterações pendentes — clica em Atualizar para recalcular."
                      : null
                  }
                />
              </MDBox>
            </ClickAwayListener>
          </MDBox>
        </Card>
      </MDBox>

      <CriarObjetivosDialogEquipas
        open={openEquipas}
        onClose={() => setOpenEquipas(false)}
        value={equipaDraft}
        vendedoresOptions={vendedoresOpt}
        refsIndividuais={(individuaisDraft || []).map((x) => String(x.refvendedor))}
        individuaisCarregados={!editando || edicaoCarregada}
        canDelete={!!equipaDraft}
        onRequestDelete={() => setOpenConfirmDelEquipa(true)}
        onApply={(eq) => {
          setEquipaDraft(eq);
          setOpenEquipas(false);
          showAlert(
            `Equipa definida: ${eq.nome} (${eq.vendedores.length} vendedor(es))`,
            "success"
          );
        }}
      />

      <CriarObjetivosDialogIndividuais
        open={openIndividuais}
        onClose={() => setOpenIndividuais(false)}
        value={individuaisDraft}
        vendedoresOptions={vendedoresOpt}
        loading={editando && (loading || !edicaoCarregada)}
        onApply={(arr) => {
          setIndividuaisDraft(arr || []);
          setOpenIndividuais(false);
          showAlert(`Objetivos individuais definidos: ${(arr || []).length}`, "success");
        }}
      />

      <Dialog
        open={openAvisoDetalhe}
        onClose={() => setOpenAvisoDetalhe(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalhe do aviso</DialogTitle>

        <DialogContent dividers>
          <MDTypography variant="body2" sx={{ mb: 2 }}>
            Objetivo do fabricante vs total definido para vendedores (Equipa + Individuais).
          </MDTypography>

          {!faltas || faltas.length === 0 ? (
            <MDTypography variant="body2">Sem diferenças.</MDTypography>
          ) : (
            <MDBox>
              {faltas.map((f) => (
                <MDBox
                  key={f.campo}
                  sx={{ mb: 1.5, p: 1, borderRadius: 2, border: "1px solid #eee" }}
                >
                  <MDTypography variant="button" sx={{ display: "block", mb: 0.5 }}>
                    {f.label}
                  </MDTypography>

                  <MDTypography variant="body2">
                    Fabricante: <b>{f.fab.toFixed(2)}</b>
                  </MDTypography>
                  <MDTypography variant="body2">
                    Vendedores (Equipa + Individuais): <b>{f.total.toFixed(2)}</b>
                  </MDTypography>
                  <MDTypography variant="body2">
                    Falta: <b>{f.falta.toFixed(2)}</b>
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="info" onClick={() => setOpenAvisoDetalhe(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openNomeNovo} onClose={() => setOpenNomeNovo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo nome</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
              Nome
            </MDTypography>
            <TextField
              value={nomeDraft}
              onChange={(e) => setNomeDraft(e.target.value)}
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="light" onClick={() => setOpenNomeNovo(false)}>
            Fechar
          </MDButton>
          <MDButton color="success" onClick={guardarNovoNome}>
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNomeEditar}
        onClose={() => setOpenNomeEditar(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar nome</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
              Nome
            </MDTypography>
            <TextField
              value={nomeDraft}
              onChange={(e) => setNomeDraft(e.target.value)}
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="light" onClick={() => setOpenNomeEditar(false)}>
            Fechar
          </MDButton>
          <MDButton color="info" onClick={guardarEditarNome}>
            Atualizar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmDelEquipa}
        onClose={() => (loadingDelEquipa ? null : setOpenConfirmDelEquipa(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover equipa</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" sx={{ fontSize: 13 }}>
            Tens a certeza que queres remover esta equipa do objetivo?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => setOpenConfirmDelEquipa(false)}
            disabled={loadingDelEquipa}
          >
            Cancelar
          </MDButton>

          <MDButton color="error" onClick={apagarEquipa} disabled={loadingDelEquipa}>
            {loadingDelEquipa ? "A remover..." : "Remover"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );

  if (embed) {
    return (
      <MDBox p={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
          <MDTypography variant="h6">
            {editando
              ? `Editar Objetivo — ${objetivoNomeSel?.nome || nomeInput || ""}`
              : "Novo Objetivo"}
          </MDTypography>

          <MDBox display="flex" gap={1}>
            <MDButton color="info" variant="outlined" onClick={() => (onClose ? onClose() : null)}>
              Fechar
            </MDButton>
          </MDBox>
        </MDBox>

        {content}
      </MDBox>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />
      {content}
    </DashboardLayout>
  );
}
